import React from 'react';
import {
  C,
  CARD_W,
  PARTICLE_SETTINGS_H,
  nx,
  ny,
} from '../flowCanvas.constants';

const HEADER_H = 40;
const LABEL_X = 12;
const CONTROL_X = 96;
const CONTROL_W = CARD_W - CONTROL_X - 12;
const ROW_H = 36;

const THEME_LABELS = {
  default: 'Default',
  christmas: 'Christmas',
  snow: 'Snow',
  easter: 'Easter',
  halloween: 'Halloween',
  party: 'Party',
  skulls: 'Skulls',
  speech: 'Speech',
  stars: 'Stars',
  stpatrick: 'St. Patrick',
  triangles: 'Triangles',
  valentines: 'Valentines',
  custom: 'Custom',
};

const APPEAR_LABELS = {
  fromTop: 'Fall From Top',
  allOver: 'Burst All Over',
  burstFromSides: 'Burst From Sides',
  burstFromBottom: 'Burst From Bottom',
};

const THEME_OPTIONS = Object.entries(THEME_LABELS)
  .filter(([value]) => value !== 'custom')
  .map(([value, label]) => ({ value, label }));

const APPEAR_OPTIONS = Object.entries(APPEAR_LABELS).map(([value, label]) => ({ value, label }));

function getFirstValue(step, liveCustomisationData) {
  const cfg = step?.customisationConfig;
  const tabKey = cfg?.tabKey;
  const varKey = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const live = tabKey && varKey && varName
    ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
    : null;
  const allValues = Array.isArray(live)
    ? live
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return allValues?.[0] || {};
}

function toBool(val) {
  if (val === true || val === false) return val;
  if (typeof val === 'string') {
    const lowered = val.trim().toLowerCase();
    if (lowered === 'true') return true;
    if (lowered === 'false') return false;
  }
  if (typeof val === 'number') {
    if (val === 1) return true;
    if (val === 0) return false;
  }
  return false;
}

function TogglePill({ x, y, width = CONTROL_W, active, label, onClick, disabled = false }) {
  return (
    <g onClick={(e) => {
      e.stopPropagation();
      if (disabled) return;
      onClick?.();
    }} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <rect
        x={x}
        y={y}
        width={width}
        height={22}
        rx="11"
        fill={disabled ? 'rgba(255,255,255,0.03)' : active ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)'}
        stroke={disabled ? 'rgba(136,146,176,0.18)' : active ? 'rgba(74,222,128,0.58)' : 'rgba(145,70,255,0.22)'}
        strokeWidth="1"
      />
      <circle cx={x + (active ? width - 11 : 11)} cy={y + 11} r="7" fill={disabled ? C.textDim : active ? C.green : C.textMuted} />
      <text
        x={x + width / 2}
        y={y + 14}
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill={disabled ? C.textDim : active ? C.green : C.text}
      >
        {label}
      </text>
    </g>
  );
}

function SelectControl({ x, y, value, options, onChange, disabled = false }) {
  return (
    <foreignObject x={x} y={y} width={CONTROL_W} height="24" style={{ pointerEvents: 'auto' }}>
      <select
        value={value}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onChange?.(e.target.value);
        }}
        style={{
          width: '100%',
          height: '22px',
          borderRadius: '11px',
          border: `1px solid ${disabled ? 'rgba(136,146,176,0.18)' : 'rgba(145,70,255,0.22)'}`,
          background: disabled ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.05)',
          color: disabled ? '#4a5570' : '#ffffff',
          fontSize: '9px',
          fontWeight: 700,
          padding: '0 8px',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </foreignObject>
  );
}

export default function ParticleSettingsNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFieldChange,
}) {
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const left = x - CARD_W / 2;
  const top = y - PARTICLE_SETTINGS_H / 2;

  const isFireworks = step?.customisationConfig?.varKey === 'fireworksSettings';

  // ── Fireworks layout ────────────────────────────────────────────────────────
  if (isFireworks) {
    const fwStyle = getFirstValue(step, liveCustomisationData);
    const useColours = toBool(fwStyle?.useColourFireworks);
    const useImages  = toBool(fwStyle?.useImageFireworks);
    const fwRowCount = 2;
    const coloursRowTop = top + HEADER_H;
    const imagesRowTop  = top + HEADER_H + ROW_H;

    return (
      <g className="fc-node fc-node--particle-settings" onClick={onClick} style={{ cursor: 'pointer' }}>
        <rect x={left + 2} y={top + 4} width={CARD_W} height={PARTICLE_SETTINGS_H} rx="10" fill="#000" fillOpacity="0.3" />
        <rect
          x={left}
          y={top}
          width={CARD_W}
          height={PARTICLE_SETTINGS_H}
          rx="10"
          fill={selected ? C.cardSel : C.card}
          stroke={selected ? C.sel : C.purple}
          strokeWidth={selected ? 2 : 1.5}
        />
        <line x1={left + 1} y1={top + HEADER_H} x2={left + CARD_W - 1} y2={top + HEADER_H} stroke={C.border} strokeWidth="1" />

        <text x={left + 14} y={top + 23} fontSize="11.5" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {step.stepTitle || 'Settings'}
        </text>
        <text x={left + 14} y={top + 36} fontSize="8.5" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          Fireworks display summary
        </text>

        {Array.from({ length: fwRowCount }, (_, index) => {
          const rowTop = top + HEADER_H + index * ROW_H;
          return (
            <line
              key={`divider-${index}`}
              x1={left + 10}
              y1={rowTop}
              x2={left + CARD_W - 10}
              y2={rowTop}
              stroke={C.border}
              strokeWidth="1"
            />
          );
        })}

        <text x={left + LABEL_X} y={coloursRowTop + 22} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          Use Colours
        </text>
        <TogglePill
          x={left + CONTROL_X}
          y={coloursRowTop + 7}
          active={useColours}
          label={useColours ? 'Enabled' : 'Disabled'}
          onClick={() => onFieldChange?.('useColourFireworks', !useColours)}
        />

        <text x={left + LABEL_X} y={imagesRowTop + 22} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          Use Images
        </text>
        <TogglePill
          x={left + CONTROL_X}
          y={imagesRowTop + 7}
          active={useImages}
          label={useImages ? 'Enabled' : 'Disabled'}
          onClick={() => onFieldChange?.('useImageFireworks', !useImages)}
        />
      </g>
    );
  }

  // ── Confetti layout (unchanged) ─────────────────────────────────────────────
  const style = getFirstValue(step, liveCustomisationData);
  const isCustomImage = style?.selectedTheme === 'custom';
  const selectedTheme = style?.selectedTheme || 'default';
  const appearStyle = style?.appearStyle || 'fromTop';
  const recolourEnabled = toBool(style?.enableRecolouring);
  const themeValue = isCustomImage ? 'default' : selectedTheme;
  const rowCount = 3;

  const useCustomRowTop = top + HEADER_H;
  const themeRowTop = top + HEADER_H + ROW_H;
  const recolourRowTop = top + HEADER_H + ROW_H;
  const appearRowTop = top + HEADER_H + ROW_H * 2;

  return (
    <g className="fc-node fc-node--particle-settings" onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect x={left + 2} y={top + 4} width={CARD_W} height={PARTICLE_SETTINGS_H} rx="10" fill="#000" fillOpacity="0.3" />
      <rect
        x={left}
        y={top}
        width={CARD_W}
        height={PARTICLE_SETTINGS_H}
        rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.5}
      />

      <line
        x1={left + 1}
        y1={top + HEADER_H}
        x2={left + CARD_W - 1}
        y2={top + HEADER_H}
        stroke={C.border}
        strokeWidth="1"
      />

      <text
        x={left + 14}
        y={top + 23}
        fontSize="11.5"
        fontWeight="700"
        fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {step.stepTitle || 'Settings'}
      </text>
      <text
        x={left + 14}
        y={top + 36}
        fontSize="8.5"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Confetti display summary
      </text>

      {Array.from({ length: rowCount }, (_, index) => {
        const rowTop = top + HEADER_H + index * ROW_H;
        return (
          <line
            key={`divider-${index}`}
            x1={left + 10}
            y1={rowTop}
            x2={left + CARD_W - 10}
            y2={rowTop}
            stroke={C.border}
            strokeWidth="1"
          />
        );
      })}

      <text x={left + LABEL_X} y={useCustomRowTop + 22} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Use Image
      </text>
      <TogglePill
        x={left + CONTROL_X}
        y={useCustomRowTop + 7}
        active={isCustomImage}
        label={isCustomImage ? 'Enabled' : 'Disabled'}
        onClick={() => onFieldChange?.('selectedTheme', isCustomImage ? 'default' : 'custom')}
      />

      {!isCustomImage && (
        <>
          <text x={left + LABEL_X} y={themeRowTop + 22} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            Theme
          </text>
          <SelectControl
            x={left + CONTROL_X}
            y={themeRowTop + 7}
            value={themeValue}
            options={THEME_OPTIONS}
            onChange={(value) => onFieldChange?.('selectedTheme', value)}
          />
        </>
      )}

      {isCustomImage && (
        <>
          <text x={left + LABEL_X} y={recolourRowTop + 22} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            Recolour
          </text>
          <TogglePill
            x={left + CONTROL_X}
            y={recolourRowTop + 7}
            active={recolourEnabled}
            label={recolourEnabled ? 'Enabled' : 'Disabled'}
            onClick={() => onFieldChange?.('enableRecolouring', !recolourEnabled)}
          />
        </>
      )}

      <text x={left + LABEL_X} y={appearRowTop + 22} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Appear
      </text>
      <SelectControl
        x={left + CONTROL_X}
        y={appearRowTop + 7}
        value={appearStyle}
        options={APPEAR_OPTIONS}
        onChange={(value) => onFieldChange?.('appearStyle', value)}
      />
    </g>
  );
}
